/**
 * Capa de animación del front — GSAP + ScrollTrigger.
 *
 * No se anima nada desde JS a mano: se marca el HTML con atributos y este
 * módulo se encarga del resto. Así la landing (o cualquier vista futura) se
 * anima sin tocar este archivo.
 *
 *   data-anim="rise"            El elemento aparece cuando entra en pantalla.
 *   data-anim-children="rise"   Aparecen sus hijos directos, uno tras otro.
 *
 * Variantes: rise (sube), pop (escala), fade (solo opacidad), left, right.
 * El valor puede omitirse: por defecto es "rise".
 *
 * Modificadores opcionales:
 *   data-anim-on="load"     Arranca al cargar en vez de al hacer scroll.
 *                           Solo para lo que está sobre la línea de flotación.
 *   data-anim-delay="0.3"   Retardo en segundos.
 *   data-anim-stagger="0.1" Separación entre hijos (solo en -children).
 *
 * El estado inicial (opacity: 0) lo pone el CSS, no el JS — si lo pusiera el
 * JS habría un parpadeo entre el primer pintado y la ejecución del bundle.
 * Ver el bloque "animaciones" al final de css/app.css y el script de arranque
 * en la plantilla front.layouts.default.
 *
 * Aparte de las entradas, el módulo monta los paneles apilados:
 *
 *   data-panel        La sección se clava arriba y lo que viene detrás entra
 *                     por encima. Solo a partir de 1024px y sin movimiento
 *                     reducido. Ver initPanels() y app.css.
 *   data-split        El titular entra palabra a palabra desde detrás de una
 *                     máscara. Para uno por página, no para cada h2.
 *   data-count-to="48"  La cifra cuenta desde cero al entrar en pantalla.
 *                     El texto del elemento es el valor final ya formateado,
 *                     así que sin JS se lee igual.
 *   data-count-format="eur"  Con dos decimales (importes). Por defecto, entero.
 *   data-count-suffix=" €"   Texto detrás de la cifra.
 *
 * Para cifras que cambian con la interacción (el total al marcar módulos) hay
 * window.rocketloomContar(el, valor): anima desde lo que muestre en ese momento.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { TextPlugin } from 'gsap/TextPlugin';
import { Physics2DPlugin } from 'gsap/Physics2DPlugin';

gsap.registerPlugin(ScrollTrigger, SplitText, TextPlugin, Physics2DPlugin);

/* Recorridos de entrada. Distancias cortas a propósito: el movimiento
   acompaña la lectura, no la interrumpe. Una variante puede traer su propia
   curva; si no la trae, usa la general. */
const VARIANTS = {
    rise: { y: 24 },
    /* drop entra desde arriba en vez de desde abajo. Con stagger, las filas de
       una lista se van posando una sobre otra: se lee como apilarlas. La curva
       frena más de golpe que la general, que es lo que da el «se posa» en vez
       de un «flota hasta su sitio». */
    drop: { y: -18, ease: 'power3.out' },
    pop: { y: 12, scale: 0.96 },
    fade: {},
    left: { x: -32 },
    right: { x: 32 },
};

const DURATION = 0.7;
const EASE = 'power2.out';
const STAGGER = 0.08;

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* A nivel de módulo: si el contexto se pierde, GSAP deja de vigilar el
   breakpoint y los pines no se deshacen al estrechar la ventana. */
const panelMedia = gsap.matchMedia();

/* Las entradas consultan la preferencia directamente y se guardan para poder
   cortarlas si el usuario la cambia sin recargar. Los paneles sí usan
   gsap.matchMedia, porque además del movimiento dependen de un ancho mínimo y
   hay que deshacerlos al cruzar el breakpoint. */
const tweens = [];

/* La animación es siempre fromTo, nunca from: el estado inicial ya viene del
   CSS, así que un from leería opacidad 0 como destino y no se vería nada. */
function reveal(targets, name, options) {
    /* La curva se saca de la variante antes de nada: lo que queda es geometría
       pura y puede volcarse tal cual en los valores de origen. */
    const { ease = EASE, ...variant } = VARIANTS[name] || VARIANTS.rise;

    const to = {
        opacity: 1,
        duration: DURATION,
        ease,
        delay: options.delay,
        stagger: options.stagger,
        /* Sin immediateRender el estado inicial lo sigue marcando el CSS hasta
           que la animación arranca de verdad: no se escriben estilos inline en
           media página nada más cargar. */
        immediateRender: false,
    };

    // Solo se devuelven a cero las propiedades que la variante mueve, para no
    // dejar un transform inline en elementos que solo hacen fundido.
    if ('x' in variant) to.x = 0;
    if ('y' in variant) to.y = 0;
    if ('scale' in variant) to.scale = 1;

    if (options.trigger) {
        to.scrollTrigger = {
            trigger: options.trigger,
            start: options.start,
            once: true,
        };
    }

    const tween = gsap.fromTo(targets, { opacity: 0, ...variant }, to);
    tweens.push(tween);

    return tween;
}

function readOptions(el) {
    const delay = parseFloat(el.dataset.animDelay);
    const stagger = parseFloat(el.dataset.animStagger);

    return {
        delay: Number.isFinite(delay) ? delay : 0,
        stagger: Number.isFinite(stagger) ? stagger : STAGGER,
        // data-anim-on="load" arranca solo; el resto espera al scroll.
        trigger: el.dataset.animOn === 'load' ? null : el,
        /* Dentro de un panel que se clava, el contenido deja de subir en cuanto
           el panel se para. Un disparo a 'top 85%' que para entonces no haya
           saltado ya no salta nunca, y ese trozo de sección se queda invisible
           para siempre. Con 'top bottom' basta con que el elemento asome por
           abajo, y eso ocurre siempre antes de que el panel se clave. */
        start: el.closest('[data-panel]') ? 'top bottom' : 'top 85%',
    };
}

/**
 * Paneles apilados.
 *
 * Cada <section data-panel> se clava arriba al llegar y lo que venga detrás
 * entra por encima. Los paneles NO tienen por qué ser contiguos: hoy son
 * Abstracts y App móvil, con Producto y Formatos en medio comportándose como
 * secciones normales. Lo que pasa por encima de un panel clavado puede ser
 * perfectamente una sección sin pin.
 *
 * El truco está en el `start`:
 *
 *   - Panel que cabe en pantalla  → se clava en cuanto su borde superior toca
 *                                   el de la ventana.
 *   - Panel más alto que la pantalla → se deja leer entero, y solo cuando su
 *                                   final alcanza el borde inferior se clava.
 *
 * Eso segundo es lo que el demo de GSAP llama «overscroll»: permite apilar
 * secciones con contenido largo sin recortarlas, porque la sección se lee
 * entera antes de quedarse clavada.
 *
 * pinSpacing: false — no se añaden espaciadores, así que la página no se hace
 * más larga de lo que ya era: el apilado es una ilusión sobre el scroll normal.
 *
 * El z-index va aquí y no en el CSS porque depende del orden de las secciones,
 * y así se deshace solo al salir del breakpoint.
 */
function initPanels() {
    const panels = gsap.utils.toArray('[data-panel]');

    if (!panels.length) {
        return; // el resto del front no lleva paneles
    }

    /* El pin cambia de sitio el contenido de la sección, así que tiene que
       existir antes que los ScrollTrigger de las entradas que viven dentro.
       Por eso initPanels() se llama antes que los reveals. */
    panelMedia.add(
        '(min-width: 1024px) and (prefers-reduced-motion: no-preference)',
        () => {
            /* z-index creciente a TODAS las secciones en orden de documento, no
               solo a los paneles. Un panel clavado pasa a `fixed`, y una
               sección normal sin z-index propio no le gana: se metería por
               debajo y no se vería. Y entre dos paneles hay secciones normales
               —Formatos pasa por encima de Abstracts clavado— así que no basta
               con numerar los paneles. */
            gsap.utils
                .toArray(panels[0].parentElement.children)
                .forEach((section, i) => {
                    gsap.set(section, { position: 'relative', zIndex: i + 1 });
                });

            panels.forEach((panel) => {
                ScrollTrigger.create({
                    trigger: panel,
                    start: () =>
                        panel.offsetHeight < window.innerHeight
                            ? 'top top'
                            : 'bottom bottom',
                    pin: true,
                    pinSpacing: false,
                    invalidateOnRefresh: true,
                });
            });
        }
    );
}


/**
 * Titular por palabras.
 *
 * SplitText parte el texto y cada palabra sube desde detrás de su propia línea
 * recortada. Es el gesto de entrada de la página, así que va uno por vista: si
 * se le pone a cada titular deja de significar nada.
 *
 * El texto original se conserva — SplitText envuelve, no reemplaza — así que si
 * el bundle no llega, el titular se lee igual.
 */
function initSplit() {
    /* Esta es la unica animacion que esconde su contenido sin que el CSS pueda
       devolverlo: las palabras quedan fuera de su mascara por transform, y si el
       navegador no da frames se quedan ahi. Pasa cuando la pagina arranca en una
       pestana de fondo y salta el temporizador de seguridad de boot(). En ese
       caso no se parte el titular: se deja tal cual, escrito y legible. */
    if (document.hidden) {
        return;
    }

    document.querySelectorAll('[data-split]').forEach((el) => {
        const split = new SplitText(el, { type: 'lines,words', linesClass: 'linea-recorte' });

        gsap.fromTo(
            split.words,
            { yPercent: 115 },
            {
                yPercent: 0,
                duration: 0.9,
                ease: 'power3.out',
                stagger: 0.055,
                delay: parseFloat(el.dataset.splitDelay) || 0.1,
            }
        );
    });
}

/**
 * Cifras que cuentan.
 *
 * El valor final vive en el HTML como texto, ya formateado; data-count-to solo
 * repite el número en crudo. Así, sin JS o con movimiento reducido, la cifra
 * está escrita y se lee. El separador de miles se reconstruye en cada paso para
 * que no baile la anchura.
 */
const FORMATOS = {
    int: new Intl.NumberFormat('es-ES', { useGrouping: 'always', maximumFractionDigits: 0 }),
    eur: new Intl.NumberFormat('es-ES', { useGrouping: 'always', minimumFractionDigits: 2, maximumFractionDigits: 2 }),
};

/* El estado vive en el propio elemento: si la cifra cambia a mitad de una
   cuenta, la siguiente arranca desde el valor que se está viendo, sin saltos. */
function contar(el, hasta, { desde, duracion = 0.6, scrollTrigger } = {}) {
    const formato = FORMATOS[el.dataset.countFormat] || FORMATOS.int;
    const sufijo = el.dataset.countSuffix || '';
    const pintar = (valor) => {
        el.textContent = formato.format(valor) + sufijo;
    };

    el.dataset.countTo = String(hasta);

    if (reduceMotion.matches) {
        pintar(hasta);
        return;
    }

    el.__contador = el.__contador || { valor: desde ?? hasta };
    if (desde !== undefined) {
        el.__contador.valor = desde;
    }

    gsap.to(el.__contador, {
        valor: hasta,
        duration: duracion,
        ease: 'power2.out',
        overwrite: true,
        onUpdate: () => pintar(el.__contador.valor),
        scrollTrigger,
    });
}

window.rocketloomContar = (el, hasta) => contar(el, hasta);

function initCounters() {
    document.querySelectorAll('[data-count-to]').forEach((el) => {
        const final = parseFloat(el.dataset.countTo);
        if (!Number.isFinite(final)) {
            return;
        }

        contar(el, final, {
            desde: 0,
            duracion: 1.4,
            scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        });
    });
}


/* === scroll -> desplaza ==================================================
 * Lo que ves responde a cuanto y como bajas.
 */

/**
 * La marquesina sigue al scroll: acelera al bajar, frena al parar y se da la
 * vuelta al subir. El CSS la mueve a velocidad fija para quien no tenga JS; aqui
 * se desactiva esa animacion y toma el mando GSAP, que si puede modularla.
 */
function initMarquesina() {
    const pista = document.querySelector('[data-marquesina] .marquesina__pista');
    if (!pista) {
        return;
    }

    pista.style.animation = 'none';

    const bucle = gsap.to(pista, {
        xPercent: -50,
        repeat: -1,
        duration: 38,
        ease: 'none',
    });

    let sentido = 1;

    ScrollTrigger.create({
        onUpdate: (self) => {
            sentido = self.direction;
            /* La velocidad viene en px/s; dividida da un multiplicador manejable
               y se limita para que un scroll brusco no la dispare. */
            const empuje = Math.min(Math.abs(self.getVelocity()) / 450, 5);
            gsap.to(bucle, {
                timeScale: (1 + empuje) * sentido,
                duration: 0.25,
                overwrite: true,
            });
            gsap.to(bucle, {
                timeScale: sentido,
                duration: 1.1,
                delay: 0.25,
                overwrite: 'auto',
            });
        },
    });
}

/* === héroe -> del caos al orden ==========================================
 * La entrada de la landing. Primero entran revueltas unas notas de caos
 * (post-its, correos, un excel) y se desvanecen; a la vez se traza el tachón
 * sobre «caos»; después se posan, una tras otra, las tarjetas ordenadas.
 *
 *   data-caos="x,y,giro"   Nota del caos: desde dónde entra y cuánto gira.
 *                          Acaba invisible; el CSS ya la tiene oculta.
 *   data-orden="x,y,giro"  Tarjeta ordenada: desde dónde se posa.
 *   data-tachon            Trazo SVG con pathLength="1" que se dibuja.
 *   data-flota             Tras la entrada, oscila arriba y abajo en bucle.
 */
function initCaos() {
    const notas = gsap.utils.toArray('[data-caos]');
    const tarjetas = gsap.utils.toArray('[data-orden]');
    const tachon = document.querySelector('[data-tachon]');

    if (!notas.length && !tarjetas.length && !tachon) {
        return;
    }

    const leer = (el, atributo) => el.getAttribute(atributo).split(',').map(Number);

    notas.forEach((nota, i) => {
        const [x, y, giro] = leer(nota, 'data-caos');
        gsap.timeline({ delay: 0.15 + i * 0.11 })
            .fromTo(nota,
                { opacity: 0, x, y, rotation: giro * 2, scale: 0.8 },
                { opacity: 1, x: 0, y: 0, rotation: giro, scale: 1, duration: 0.5, ease: 'power2.out' })
            .to(nota, { x: -x * 0.15, y: -y * 0.1, rotation: -giro * 0.6, duration: 0.75, ease: 'sine.inOut' })
            .to(nota, { x: x * 0.1, y: y * 0.1, rotation: giro, duration: 0.4, ease: 'sine.inOut' })
            .to(nota, { opacity: 0, x: 0, y: 30, rotation: giro * 2, scale: 0.5, filter: 'blur(8px)', duration: 0.65, ease: 'power2.in' });
    });

    if (tachon) {
        tweens.push(gsap.fromTo(tachon,
            { strokeDashoffset: 1 },
            { strokeDashoffset: 0, duration: 0.65, delay: 1.1, ease: 'power2.inOut' }));
    }

    tarjetas.forEach((tarjeta, i) => {
        const [x, y, giro] = leer(tarjeta, 'data-orden');
        tweens.push(gsap.fromTo(tarjeta,
            { opacity: 0, x, y, rotation: giro, scale: 0.9 },
            { opacity: 1, x: 0, y: 0, rotation: 0, scale: 1, duration: 1.1, delay: 1.65 + i * 0.13, ease: 'expo.out' }));
    });

    // La flotación empieza cuando ya se han posado todas.
    gsap.utils.toArray('[data-flota]').forEach((el, i) => {
        gsap.to(el, {
            y: -7,
            duration: 1.3 + i * 0.225,
            delay: 2.9,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
        });
    });
}

/**
 * Contador «en directo» de la tarjeta de inscripciones: suma de 1 a 3 cada
 * pocos segundos. Es atrezo, así que solo corre con movimiento permitido.
 */
function initEnVivo() {
    const total = document.querySelector('[data-en-vivo]');
    const hoy = document.querySelector('[data-en-vivo-hoy]');
    if (!total) {
        return;
    }

    const formato = new Intl.NumberFormat('es-ES', { useGrouping: 'always' });
    let valor = parseInt(total.dataset.enVivo, 10);
    let valorHoy = hoy ? parseInt(hoy.dataset.enVivoHoy, 10) : 0;

    setInterval(() => {
        const suma = 1 + Math.floor(Math.random() * 3);
        valor += suma;
        valorHoy += suma;
        total.textContent = formato.format(valor);
        if (hoy) {
            hoy.textContent = valorHoy;
        }
    }, 2600);
}

/**
 * Barras de progreso que se llenan al entrar en pantalla. El ancho final vive
 * en el estilo del propio elemento, así que sin JS ya se ven llenas.
 */
function initBarras() {
    gsap.utils.toArray('[data-barra]').forEach((barra) => {
        tweens.push(gsap.fromTo(barra,
            { width: '0%' },
            {
                width: barra.dataset.barra + '%',
                duration: 1.4,
                delay: 0.4,
                ease: 'expo.out',
                scrollTrigger: { trigger: barra, start: 'top 90%', once: true },
            }));
    });
}

/* === estado -> informa =================================================== */

/**
 * El boton de un formulario dice lo que esta pasando. El envio es un POST normal
 * con redireccion, asi que un "Enviado" no se veria nunca: lo que si dura es el
 * "Enviando..." de la ida y vuelta. De paso se bloquea el boton, que evita el
 * doble envio -- el problema de verdad en formularios con captcha lento.
 *
 * Esto no es decoracion, es informacion: se monta tambien con movimiento
 * reducido y tambien en pestana oculta.
 */
function initEstadoFormulario() {
    document.querySelectorAll('[data-estado-envio]').forEach((formulario) => {
        const boton = formulario.querySelector('button[type="submit"]');
        if (!boton) {
            return;
        }
        /* Se anima la etiqueta, no el boton: dentro hay un icono que se perderia. */
        const etiqueta = boton.querySelector('[data-estado-etiqueta]') || boton;

        formulario.addEventListener('submit', () => {
            if (boton.dataset.enviando) {
                return;
            }
            /* Si el formulario tiene jQuery Validation (proengsoft/laravel-
             * jsvalidation) y el envio esta bloqueado, no hay "Enviando...":
             * sin este guard el boton se queda animado indefinidamente aunque
             * el formulario nunca llegue a mandarse. */
            if (window.jQuery && jQuery.fn.valid && !jQuery(formulario).valid()) {
                return;
            }
            boton.dataset.enviando = '1';
            boton.setAttribute('aria-busy', 'true');
            boton.style.pointerEvents = 'none';
            boton.style.opacity = '0.75';

            const destino = formulario.dataset.estadoEnvioTexto || 'Enviando…';

            if (reduceMotion.matches) {
                etiqueta.textContent = destino;
                return;
            }

            gsap.to(etiqueta, {
                duration: 0.45,
                text: { value: destino, delimiter: '' },
                ease: 'none',
            });
        });
    });
}

/* === submit -> celebra =================================================== */

/**
 * Particulas con Physics2D cuando el envio ha ido bien. Va en el acuse de recibo,
 * no en el clic: ahi si ha pasado algo. Celebrar antes de tiempo solo resta
 * seriedad, y ademas el clic se lleva la pagina por delante.
 *
 * Las particulas se crean y se destruyen: no queda nada en el DOM al terminar.
 */
function initCelebracion() {
    const diana = document.querySelector('[data-celebra]');
    if (!diana || reduceMotion.matches) {
        return;
    }

    const caja = diana.getBoundingClientRect();
    const origenX = caja.left + caja.width / 2;
    const origenY = caja.top + caja.height / 2;
    const colores = ['#22C7B8', '#635BFF', '#25245E'];
    const capa = document.createElement('div');

    capa.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:60;overflow:hidden';
    document.body.appendChild(capa);

    for (let i = 0; i < 46; i++) {
        const trozo = document.createElement('span');
        const lado = gsap.utils.random(6, 11);
        trozo.style.cssText =
            'position:absolute;border-radius:9999px;will-change:transform;' +
            'width:' + lado + 'px;height:' + lado + 'px;' +
            'background:' + colores[i % colores.length] + ';' +
            'left:' + origenX + 'px;top:' + origenY + 'px;';
        capa.appendChild(trozo);

        gsap.to(trozo, {
            duration: gsap.utils.random(1.1, 1.9),
            physics2D: {
                velocity: gsap.utils.random(260, 620),
                angle: gsap.utils.random(200, 340),
                gravity: 900,
            },
            opacity: 0,
            ease: 'none',
        });
    }

    gsap.delayedCall(2.2, () => capa.remove());
}

/* Salto al estado final y fin de la animación: lo que se usa si el usuario
   activa "reducir movimiento" con la página ya abierta. */
function finishAll() {
    tweens.forEach((tween) => {
        if (tween.scrollTrigger) {
            tween.scrollTrigger.kill();
        }
        tween.progress(1).kill();
    });
    tweens.length = 0;
}

function start() {
    if (reduceMotion.matches) {
        return;
    }

    initPanels();
    initSplit();
    initCounters();
    initCaos();
    initEnVivo();
    initBarras();
    initMarquesina();
    initCelebracion();

    document.querySelectorAll('[data-anim]').forEach((el) => {
        reveal(el, el.dataset.anim, readOptions(el));
    });

    document.querySelectorAll('[data-anim-children]').forEach((el) => {
        if (el.children.length) {
            reveal(el.children, el.dataset.animChildren, readOptions(el));
        }
    });

    reduceMotion.addEventListener('change', (event) => {
        if (event.matches) {
            finishAll();
        }
    });

    /* Las webfonts cambian las alturas después del primer cálculo, así que las
       posiciones de disparo hay que recalcularlas cuando terminan de cargar. */
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => ScrollTrigger.refresh());
    }
}

/* En una pestaña de segundo plano el navegador no da frames, así que una
   animación lanzada ahí se queda congelada a medias y el visitante se
   encuentra la página a medio aparecer al volver. Si la pestaña no está a la
   vista se espera: la entrada se reproduce entera cuando alguien la mira. */
let started = false;

function startOnce() {
    if (started) {
        return;
    }
    started = true;
    start();
}

function boot() {
    /* El estado del formulario no es decoracion: se monta siempre, antes
       incluso de mirar si la pestana esta a la vista. */
    initEstadoFormulario();

    /* Le dice al script de arranque de la plantilla que el bundle ha llegado y
       que no hace falta su red de seguridad. Se marca ya, antes de la espera:
       si no, el temporizador destaparía la página y la animación la volvería a
       ocultar al arrancar. */
    document.documentElement.setAttribute('data-anim-ready', '');

    if (!document.hidden) {
        startOnce();
        return;
    }

    /* Varias señales para lo mismo, porque mientras la pestaña siga oculta el
       contenido está en opacidad 0 y perder el aviso dejaría la página en
       blanco. La primera que llegue arranca; startOnce ignora el resto. */
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            startOnce();
        }
    });
    window.addEventListener('focus', startOnce, { once: true });
    window.addEventListener('pageshow', startOnce, { once: true });
    setTimeout(startOnce, 10000);
}

if (document.readyState === 'loading') {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
    boot();
}
